import { Core } from "@faire/design-tokens";
import { useQuery as usePublishedProductCollectionMetadata } from "@faire/web-api/api/brand/brandToken/published-product-collection-metadata/get-hooks";
import { QueryParameters as IProductBasedCollectionTokenQueryParams } from "@faire/web-api/api/product-based-collections/productBasedCollectionToken/retailer/get";
import { useQuery as useProductBasedCollectionTokenQuery } from "@faire/web-api/api/product-based-collections/productBasedCollectionToken/retailer/get-hooks";
import { trackCoreCollectionsQuickAddCollectionPageCheckoutClick } from "@faire/web-api/events/coreCollections/click/quickAddCollectionPageCheckout";
import { trackCoreCollectionsStickyCartCollectionPageViewBagClick } from "@faire/web-api/events/coreCollections/click/stickyCartCollectionPageViewBag";
import { trackCoreCollectionsCollectionsPageStickyCartView } from "@faire/web-api/events/coreCollections/view/collectionsPageStickyCart";
import { trackCoreCollectionsProductBasedCollectionPageView } from "@faire/web-api/events/coreCollections/view/productBasedCollectionPage";
import { IProductBasedCollectionMetadata } from "@faire/web-api/indigofair/data/IProductBasedCollectionMetadata";
import { QueryParameters as ProductBasedCollectionTokenQueryParameters } from "@faire/web-api/routes/product-based-collection/product_based_collection_token";
import { useStrictLocalization } from "@faire/web/common/localization";
import { trackEvent } from "@faire/web/common/trackEvent";
import { Row } from "@faire/web/slate/Layout";
import { PageLoader } from "@faire/web/ui/CircleSpinner";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { observer } from "mobx-react";
import React, { Suspense, useEffect, useState } from "react";
import styled from "styled-components";

import { useAppSignUpPageResultsUpdateEffect } from "@faire/retailer/components/AppSignUp/AppSignUpPageResultsProvider";
import { RetailerFooter } from "@faire/retailer/components/Footer/RetailerFooter";
import { AddedToBagContainer } from "@faire/retailer/components/Notifications/AddedToBag/AddedToBagStore";
import { useAddedToBag } from "@faire/retailer/components/Notifications/AddedToBag/useAddedToBag";
import { useCartProductAddOptions } from "@faire/retailer/features/ProductAdd/useCartProductAddOptions";
import { LazyQuickAddModal } from "@faire/retailer/features/ProductQuickAddModal/LazyQuickAddModal";
import { useCartUpdate } from "@faire/retailer/hooks/cart/useCartUpdate";
import { canAddToCart } from "@faire/retailer/lib/canAddToCart";
import { getCartToken } from "@faire/retailer/serialized-data/getCartToken";
import {
  trackEditCartItemEvent,
  trackProductClick,
} from "@faire/retailer/services/Events";
import { useTopOfPageContentStore } from "@faire/retailer/stores/ui/TopOfPageContentStore";
import {
  BrandCartEventHandler,
  BrandCartEvents,
} from "@faire/retailer/views/brand/BrandCart";
import { BrandCartViewState } from "@faire/retailer/views/brand/BrandCart/BrandCartViewState";
import { LazyBrandCart } from "@faire/retailer/views/brand/BrandCart/lazyBrandCart";
import { InnerMakerContainer as ProductBasedCollectionInnerContainer } from "@faire/retailer/views/brand/InnerMakerContainer";
import { MakerContainer as ProductBasedCollectionContainer } from "@faire/retailer/views/brand/styled";
import { DisplayStickyActionBanner } from "@faire/retailer/views/product-based-collection/__internal__/DisplayCollectionPromo/DisplayStickyActionBanner";
import { ProductBasedCollectionBreadcrumb } from "@faire/retailer/views/product-based-collection/__internal__/ProductBasedCollectionBreadcrumb";
import { ProductBasedCollectionPageContent } from "@faire/retailer/views/product-based-collection/__internal__/ProductBasedCollectionPageContent";

import { useHideUpdateCartModal } from "../../brand/actions/useHideUpdateCartModal";
import { useFetchDisplayPromoProgress } from "../../brand/BrandCart/actions/useFetchDisplayPromoProgress";
import { useIsCollectionDisplayPromoActive } from "../../brand/BrandCart/hooks/useIsCollectionDisplayPromoActive";
import { useIsQualifiedForCollectionDisplayPromo } from "../../brand/BrandCart/hooks/useIsQualifiedForCollectionDisplayPromo";
import { BrandPageBrandViewClientContextProvider } from "../../brand/contexts/BrandPageBrandViewContext";
import {
  BrandPageCartStateContext,
  useBrandPageCartStateContext,
} from "../../brand/contexts/BrandPageCartStateContext";
import { BrandPageStateContext } from "../../brand/contexts/BrandPageStateContext";
import { useBrandPageStateData } from "../../brand/contexts/useBrandPageStateData";
import { useBrand } from "../../brand/hooks/useBrand";
import { useBrandPageModal } from "../../brand/hooks/useBrandPageModal";
import { useProductTokenForUpdateCartModal } from "../../brand/hooks/useProductTokenForUpdateCartModal";
import { BrandPageModal } from "../../brand/utils/enum";

import { ProductBasedCollectionErrorContent } from "./ProductBasedCollectionErrorContent";

const EMPTY_BRAND_TOKEN = "EMPTY";
const EMPTY_PBC_TOKEN = "EMPTY";
interface IProductBasedCollectionProps {
  token: string;
}

const ProductBasedCollectionBase: React.FC<IProductBasedCollectionProps> = ({
  token,
}) => {
  const { strictLocalize } = useStrictLocalization();

  const { highlightedProduct } = useQueryParams(
    ProductBasedCollectionTokenQueryParameters.parse
  );

  const { data: collectionData, isError: isCollectionError } =
    useProductBasedCollectionTokenQuery(
      token,
      IProductBasedCollectionTokenQueryParams.build({
        highlighted_product: highlightedProduct,
      })
    );

  const collection = collectionData?.product_collection;

  useAppSignUpPageResultsUpdateEffect({ productBasedCollection: collection });

  const [brandCartViewState, setBrandCartViewState] =
    useState<BrandCartViewState>();

  const { isDesktopAndAbove } = useViewport();

  const { updateCartItems } = useCartUpdate();

  const brandPageStateData = useBrandPageStateData(
    collection?.brand_token ?? ""
  );

  useEffect(() => {
    if (collection?.brand_token) {
      const oldBrandCartViewState = brandCartViewState;
      setBrandCartViewState(
        new BrandCartViewState(
          collection.brand_token,
          updateCartItems,
          undefined,
          true,
          collection
        )
      );
      if (oldBrandCartViewState !== undefined) {
        brandCartViewState?.cleanup();
      }

      if (canAddToCart() && isDesktopAndAbove) {
        trackCoreCollectionsCollectionsPageStickyCartView();
      }

      trackCoreCollectionsProductBasedCollectionPageView(
        collection.brand_token,
        token,
        collectionData?.collection_promo?.brand_promo_collection_token ?? ""
      );
    }
    // UpdateCartItem function is causing flaky sticky cart on collection page as its references changes every time the cart being modified. Will ignore this dependency before we get rid of brand cart view state.
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection?.brand_token, isDesktopAndAbove, token]);

  useEffect(() => {
    return () => {
      brandCartViewState?.cleanup();
    };
  }, [brandCartViewState]);

  if (isCollectionError) {
    const errorMessage = strictLocalize(
      {
        defaultMessage: "Failed to fetch collection {apparelCollectionToken}",
        description: {
          text: "Error message to communicate to the user that the website was unable to load the data required to show that user the selected apparel product collection.",
          img: "https://cdn.faire.com/fastly/7f58df7a72419f6ccd0fa0e30003a41050f2bad0828d50db8e972a79e3003647.png",
        },
      },
      {
        apparelCollectionToken: token,
      }
    );

    return (
      <>
        <ProductBasedCollectionErrorContent errorMessage={errorMessage} />
        <RetailerFooter />
      </>
    );
  }

  if (collection === undefined) {
    return <PageLoader />;
  }

  if (brandCartViewState === undefined) {
    return null;
  }

  return (
    <BrandPageStateContext.Provider value={brandPageStateData}>
      <BrandPageBrandViewClientContextProvider
        brandToken={collection?.brand_token ?? ""}
      >
        <BrandPageCartStateContext.Provider value={{ brandCartViewState }}>
          <ProductBaseCollectionContent token={token} />
        </BrandPageCartStateContext.Provider>
      </BrandPageBrandViewClientContextProvider>
    </BrandPageStateContext.Provider>
  );
};

const ProductBaseCollectionContent: React.FC<{
  token: string;
}> = ({ token }) => {
  const { highlightedProduct } = useQueryParams(
    ProductBasedCollectionTokenQueryParameters.parse
  );
  const { headerPlusPromoBannerHeight } = useTopOfPageContentStore([
    "headerPlusPromoBannerHeight",
  ]);
  const { data: collectionData } = useProductBasedCollectionTokenQuery(
    token,
    IProductBasedCollectionTokenQueryParams.build({
      highlighted_product: highlightedProduct,
    })
  );

  const collection = collectionData?.product_collection;
  const productTiles = collectionData?.product_tiles ?? [];
  const showVerticalImagery = collectionData?.show_vertical_imagery ?? true;

  const { data: collectionMetaData } = usePublishedProductCollectionMetadata(
    collectionData?.product_collection?.brand_token ?? "",
    undefined,
    { enabled: !!collectionData?.product_collection?.brand_token }
  );

  const otherCollections: IProductBasedCollectionMetadata[] =
    collectionMetaData?.product_based_collections?.filter(
      (thisCollection) =>
        thisCollection.product_based_collection_token !== collection?.token
    ) ?? [];

  const { brandCartViewState } = useBrandPageCartStateContext();
  const isQualifiedForCollectionDisplayPromo =
    useIsQualifiedForCollectionDisplayPromo();
  const isCollectionDisplayPromoActive = useIsCollectionDisplayPromoActive();

  const { isDesktopAndAbove, isMobile, isTabletAndBelow } = useViewport();

  const { ignoreContainer } = useAddedToBag();
  const productAddOptions = useCartProductAddOptions(
    brandCartViewState?.updateItems
  );
  const brand = useBrand();
  const brandPageModal = useBrandPageModal();
  const productTokenForUpdateCartModal = useProductTokenForUpdateCartModal();
  const fetchDisplayPromoProgress = useFetchDisplayPromoProgress();

  useEffect(() => {
    fetchDisplayPromoProgress();
  }, [fetchDisplayPromoProgress]);

  const onHideUpdateCartModal = useHideUpdateCartModal();

  const viewRef = useDoActionWhenVisible(() => {
    ignoreContainer(AddedToBagContainer.BRAND_CART, !isMobile);
  });

  const handleBrandCartEvents: BrandCartEventHandler = ({
    event,
    token,
    quantities,
    message,
  }) => {
    switch (event) {
      case BrandCartEvents.OPEN_PRODUCT_MODAL:
        if (token) {
          trackProductClick(token, "collection_page_sidebar_cart_cart_item");
        }
        break;
      case BrandCartEvents.CHANGE_CART_QUANTITY:
        if (quantities && token) {
          const containerName =
            quantities.newValue === 0
              ? "collection_page_sidebar_cart"
              : "collection_page_sidebar_cart_dropdown";
          trackEditCartItemEvent(
            quantities.oldValue,
            quantities.newValue,
            containerName,
            token
          );
        }
        break;
      case BrandCartEvents.GO_TO_BAG:
        trackCoreCollectionsStickyCartCollectionPageViewBagClick(
          collection?.token ?? EMPTY_PBC_TOKEN,
          collection?.brand_token ?? EMPTY_BRAND_TOKEN
        );
        break;
      case BrandCartEvents.CHECKOUT:
        trackCoreCollectionsQuickAddCollectionPageCheckoutClick(
          collection?.token ?? EMPTY_PBC_TOKEN,
          collection?.brand_token ?? EMPTY_BRAND_TOKEN,
          getCartToken()
        );
        break;
      case BrandCartEvents.CHECKOUT_ERROR:
        trackEvent(`/collection/cart/checkout-button/shake/${message}`);
        break;
    }
  };

  if (collection === undefined) {
    return <PageLoader />;
  }

  return (
    <>
      <ProductBasedCollectionUpperPart
        ref={viewRef}
        data-test-id="collections-page-sticky-cart-wrapper"
      >
        {brandPageModal === BrandPageModal.UPDATE_CART &&
        productTokenForUpdateCartModal ? (
          <Suspense fallback={null}>
            <LazyQuickAddModal
              onClose={onHideUpdateCartModal}
              productToken={productTokenForUpdateCartModal}
              productAddOptions={productAddOptions}
              updateCartFn={brandCartViewState.updateItems}
            />
          </Suspense>
        ) : null}
        <ProductBasedCollectionContainer>
          <ProductBasedCollectionInnerContainer>
            <ProductBasedCollectionBreadcrumb collection={collection} />
            <ProductBasedCollectionPageContent
              collection={collection}
              otherCollections={otherCollections}
              productTiles={productTiles}
              showVerticalImagery={showVerticalImagery}
              collectionPromo={collectionData?.collection_promo}
            />
          </ProductBasedCollectionInnerContainer>
        </ProductBasedCollectionContainer>
        {canAddToCart() && isDesktopAndAbove && brand ? (
          <Suspense fallback={null}>
            <LazyBrandCart
              top={headerPlusPromoBannerHeight}
              brandToken={brand.token}
              onEvent={handleBrandCartEvents}
            />
          </Suspense>
        ) : null}
      </ProductBasedCollectionUpperPart>
      <RetailerFooter />
      {isTabletAndBelow &&
      isCollectionDisplayPromoActive &&
      isQualifiedForCollectionDisplayPromo ? (
        <DisplayStickyActionBanner
          collection={brandCartViewState.productBasedCollection!}
          updateItems={brandCartViewState.updateItems}
        />
      ) : null}
    </>
  );
};

export const ProductBasedCollection = observer(ProductBasedCollectionBase);

const ProductBasedCollectionUpperPart = styled(Row).attrs({
  align: "flex-start",
})`
  width: 100%;
  margin: 0 auto;
  padding-bottom: 0;
  border-bottom: 1px solid ${Core.surface.subdued};
`;
